import { getChatContext } from '@/services/chat';
import client from '@/store/client';
import navbarStore from '@/store/navbar';
import slsLogger from '@public/log/sls';
import { makeAutoObservable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { HelperCore } from './dependencies';
import globalStore from '@/store/global';
import { ModelItem } from '@/components/model-management/store';

const DEFAULT_MAXCOUNT = 4096 * 1.5;

declare global {
  interface Window {
    _apiRobot: APIRobotStore;
    _wsAPIClientCbTags: (params: { data?: string }) => void;
    _wsAPIClientMsg: (params: { data: string; succ?: boolean }) => void;
    [key: `onApiCheckMessage${number}`]: (res: { data: string }) => void;
  }
}

export interface Message {
  id: number;
  event: number;
  retry: string;
  data: string;
  docs?: any;
  refer_search?: any;
  search_total?: number;
  search_keyword?: string;
  file?: any;
}

export interface SingleMessage {
  id?: string;
  choices?: Array<{
    finish_reason?: string;
    delta?: {
      content?: string;
      reasoning_content?: string;
    };
  }>;
  done?: boolean;
}

class APIRobotStore {
  APIModels = [];
  count = 0;
  timer: ReturnType<typeof setTimeout>;
  isThink = false;
  thinkType = '';
  isDone = true;
  conversationId = '';
  dataset = null;
  thinkStartTime = 0;
  lastDocs = [];

  // 服务端存储用，描述文档和msg的对应关系msgId，文档模式使用
  msgId = null;

  // 联网搜索素材,生成上下文和历史记录用的
  referSearch = null;
  searchKeyword = '';
  searchTotal = 0

  // 用户选择的附件，生成上下文和历史记录用的
  file = null;

  onopen = () => { };
  onmessage = (msg: Message) => { };
  onclose = () => { };
  onerror = (err?) => { };

  initReady = false;
  initReadyCallback = [];

  constructor() {
    makeAutoObservable(this);

    if (!HelperCore.IS_PROD) {
      window._apiRobot = this;
    }
  }

  start() {
    client.run('GetOllamaInfo', res => {
      if (res?.hasollama && res?.isollamaready) {
        // 获取模型列表
        client.run("UniapiQuery", (data, params) => {
          // console.log('api/tags', data, params)
        }, {
          api: "/v1/models",
          method: 1,
          callback: "_wsAPIClientCbTags"
        });
      }
    })
  }

  query(chatModel: string, msg: any[], apikey: string, {
    protocol = 1,
    reqURL,
    callback,
    noStream = false,
    ext,
  }: {
    protocol?: number;
    reqURL?: string;
    callback?: string;
    ext?: { reasoning?: boolean };
    noStream?: boolean;
  } = {}) {
    this.count = 0;
    this.isThink = false;
    this.thinkStartTime = 0;
    //2分钟没收到，就终止
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (this.count === 0) {
        this.stop();
      }
    }, 2 * 60 * 1000);
    let headSize = 2;
    const heads: Record<`head${bigint}`, string> = {
      head1: `Authorization:Bearer ${apikey}`,
      head2: 'Content-Type:application/json',
    };
    if (reqURL) {
      headSize = 4;
      heads.head3 = 'req-type:' + ['openai', 'gemini', 'anthropic', 'azure'][protocol - 1];
      heads.head4 = `req-url:${reqURL}`;
      if (reqURL.startsWith('https:')) {
        headSize = 5;
        heads.head5 = 'Uniapi-verify-https:false';
      }
    }
    if (protocol === 3) {
      headSize++;
      heads[`head${headSize}`] = 'anthropic-version:2023-06-01';
    }
    if (ext) {
      if (ext?.reasoning) {
        headSize++;
        heads[`head${headSize}`] = 'Uniapi-reasoning-model:true';
      }
    }

    const stream = !reqURL || !noStream;

    const sendParams = {
      api: "/v1/chat/completions",
      method: 0,
      timeout: 0, // 不限时间
      heads_size: headSize,
      heads,
      param: {
        model: chatModel,
        stream,
        messages: msg,
      },
      stream,
      callback: globalStore.isKownledgeChat() ? "_wsAPIClientMsg2" : (callback || '_wsAPIClientMsg'),
    };

    HelperCore.log('调用Uniapi', protocol, sendParams);
    
    client.run("UniapiQuery", () => { }, sendParams);
  }

  async checkAPIKey(apiKey: string, modelName: string, reqURL: string, protocol: number, noStream = false) {    
    let count = 0;
    let hasThink = false;
    const thinkError = await new Promise((resolve, reject) => {
      const callback = 'onApiCheckMessage' + Date.now();
      window[callback] = (res) => {
        HelperCore.log('onApiCheckMessage', res);
        if (!res.data) return;
        if (!res.data.includes('content')) {
          if (count > 0) {
            resolve(false);
          } else {
            reject();
          }
          return;
        }
        if (noStream) {
          resolve(/"reasoning_content":"[^"]+|<\/?think>/.test(res.data));
          return;
        } else {
          const regex = /^data: (.+)$/gm;
          const matches = [...res.data.matchAll(regex)].map(m => m[1]); // 提取匹配的 JSON 内容
          if (matches.some((m) => {
            try {
              const json: SingleMessage = JSON.parse(m);
              if (!json.id) {
                if (json.choices[0]?.finish_reason) {
                  resolve(false);
                  return true;
                }
                return false;
              }
              const choice = json.choices?.[0];
              if (!choice) {
                if (count > 0) {
                  resolve(false);
                  return true;
                }
              }
              if (choice.finish_reason) {
                resolve(false);
                return true;
              }
              if (choice.delta?.reasoning_content || choice.delta?.content.includes('<think>')) {
                hasThink = true;
                resolve(false);
                return true;
              }
              if (choice.delta?.content.includes('</think>')) {
                hasThink = false;
                resolve(true);
                return true;
              }
              count++;
              return false;
            } catch {
              return true;
            }
          })) {
            this.stop();
          }
        }
      };
      // console.log(reqURL);
      this.query(modelName, [{ role: "user", content: "Testing, return 'ok'." }], apiKey, {
        protocol, reqURL, callback, noStream,
      });
    });
    if (!thinkError && !hasThink && !noStream) {
      return this.checkAPIKey(apiKey, modelName, reqURL, protocol, true);
    }
    return thinkError;
  }

  async fetch(params: {
    role: number;
    annex_msg_id: string;
    conversation_id?: string;
    dataset?: string;
    prompt: string;
    agent_id: string;
    file?: any;
    is_so?: boolean;
    ks_id?: string;
  }, options) {
    // debug
    HelperCore.log('apiRobot chatBegin:', params);
    
    this.onopen = options.onopen;
    this.onmessage = options.onmessage;
    this.onclose = options.onclose;
    this.onerror = options.onerror;
    this.isDone = false;

    const { conversation_id, role, annex_msg_id, dataset, prompt, agent_id, file, is_so, ks_id } = params;
    const chatModel: ModelItem = navbarStore.chatModels[role];

    this.conversationId = conversation_id ? conversation_id : uuidv4().replace(/-/g, '');
    if (dataset && !this.dataset) {
      this.dataset = dataset;
    }

    this.msgId = `${+new Date}`;

    if(this.conversationId){
      options.onmessage({
        id: 0,
        event : 100,
        data : `CONVERSATIONID####${this.conversationId}`,
        retry: '',
      });
    }

    // 从服务端获取上下文
    const res = await getChatContext({
      conversation_id: this.conversationId,
      annex_msg_id,
      dataset: dataset || this.dataset,
      prompt,
      max_total: DEFAULT_MAXCOUNT,
      agent_id,
      file,
      role_biz: file && file.length ? 'doc' : 'common',
      msg_id: this.msgId,
      model_id: role,
      is_so,
      ks_id,
      model_type: chatModel.chat_type === 100 && (chatModel.model_type === 3 || chatModel.model_type === 4) ? chatModel.model_type : 2, // 1-本地模型 2-api模型 3-局域网模型 4-一体机
    });

    this.lastDocs = res?.rag?.docs || []

    this.referSearch = res?.refer_search
    this.searchKeyword = res?.search_keyword;
    this.searchTotal = res?.search_total;

    this.file = file;
    HelperCore.log('chatModel:', chatModel);

    this.query(chatModel.apply_model || chatModel.name, res?.messages, chatModel.key?.api_key, {
      protocol: chatModel?.protocol,
      reqURL: chatModel?.api_url,
      ext: chatModel.ext,
    });
  }

  updateModels(modelsStr) {
    try {
      const models = JSON.parse(modelsStr).data;
      this.APIModels = models;
      navbarStore.setChatModels(models);
    } catch (e) {
      slsLogger.log({
        action: "api_chat",
        arrt: "error",
        ext: "model_list_parse_error",
      });
    }
  }

  handleSingleMessage(json: SingleMessage) {
    console.log('>>>cb', json);
    clearTimeout(this.timer);
    if (!json?.id) {
      if (json?.choices[0]?.finish_reason) {
        if (!this.isDone) {
          this.isDone = true;
          this.onclose();
        }
      }
      return;
    }

    if (!json?.choices?.length) {
      if (!this.isDone && this.count > 0) {
        // 如果已经收到过消息，则结束会话。
        this.isDone = true;
        this.onclose();
      } else {
        // 否则加定时器，2秒钟内没有再收到消息，中断会话。
        this.timer = setTimeout(() => {
          this.stop();
        }, 2000);
      }
      // 遇到内容为空的情况，不执行处理逻辑
      return;
    }

    const choice = json?.choices?.[0];
    // if (choice?.finish_reason || !json?.choices?.length) {
    if (choice?.delta) {
      this.count++;
      if (this.count === 1) {

        // const msg1 = {
        //   id: this.count,
        //   event: 100,
        //   retry: '',
        //   data: `CONVERSATIONID####${this.conversationId}`,
        // }
        // this.onmessage?.(msg1);

        this.count++;
        const msg2 = {
          id: this.count,
          event: 101,
          retry: '',
          data: `MESSAGEID####${this.msgId}`,
          docs: this.lastDocs,
          refer_search: this.referSearch,
          search_total: this.searchTotal,
          search_keyword: this.searchKeyword,
          file: this.file,
        };
        this.onmessage?.(msg2);
      }
      let event = 200;

      let content = choice?.delta?.content || '';
      let reasoningContent = choice?.delta?.reasoning_content

      console.log('>>>content:', choice?.delta?.content);
      console.log('>>>reasoning_content:', choice?.delta?.reasoning_content);

      if ((!this.isThink && reasoningContent) || content === '<think>') {
        this.isThink = true;
        this.thinkStartTime = +new Date;
        this.thinkType = content === '<think>' ? 'tag' : 'reason';

        if(content === '<think>') {
          return
        }
      }

      if ((this.isThink && !reasoningContent && this.thinkType === 'reason') || (content === '</think>' && this.thinkType === 'tag')) {
        this.isThink = false;
        event = 102;
        
        content = JSON.stringify({
          type: "reasoning_duration",
          message: (((+new Date) - this.thinkStartTime) / 1000).toFixed(1),
        })
      }

      if (this.isThink) {
        event = 102;
        content = JSON.stringify({
          type: "reasoning_text",
          message: this.thinkType === 'tag' ? content : reasoningContent,
        })
      }
      const msg = {
        id: this.count,
        event,
        retry: '',
        data: content,
      }
      if (event === 200 && !content && this.count === 2) {
        // 过滤掉空数据
        this.count--;
        return;
      }
      this.onmessage?.(msg);

      if (json?.done || choice?.finish_reason) {
        if (!this.isDone) {
          console.log('>>>done');
          this.isDone = true;
          this.onclose();
        }
      }
    } else {
      if (choice?.finish_reason) {
        if (!this.isDone) {
          this.isDone = true;
          this.onclose();
        }
      } else {
        this.onerror();
      }
    }
  }
  message(data: {
    data: string;
    succ?: boolean;
  }) {
    clearTimeout(this.timer);

    // 正则匹配 JSON
    const regex = /^data: (.+)$/gm;
    const matches = [...data?.data.matchAll(regex)].map(m => m[1]); // 提取匹配的 JSON 内容

    matches.forEach(str => {
      try {
        const json = JSON.parse(str);
        this.handleSingleMessage(json)
      } catch (error) {
        // 不是json的 情况
        if (/done/i.test(str)) {
          this.isDone = true;
          this.onclose();
          return
        }
        console.error("JSON 解析失败:", error, str);
      }
    })
  }

  stop() {
    client.run('BlockUniapiQuery', res => {
      console.log('>>>BlockUniapiQuery', res);
    });
    this.onclose();
  }

  destory() {
    if (!this.isDone) {
      this.stop();
    } else {
      this.onclose();
    }
  }

  whenInitReady (fn = () => {}) {
    if(this.initReady){
      try{
        fn();
      } catch(e){}
    }
    else {
      this.initReadyCallback.push(fn);
    }
  }
  doInitReady () {
    if(this.initReadyCallback.length){
      this.initReadyCallback.forEach(fn => {
        try{
          fn();
        } catch(e){}
      });
      this.initReadyCallback = [];
    }
  }
}

const apiRobot = new APIRobotStore();

window._wsAPIClientCbTags = (res) => {
  if (res?.data) {
    apiRobot.updateModels(res.data);
  }

  apiRobot.initReady = true;
  apiRobot.doInitReady();
}

window._wsAPIClientMsg = (res) => {
  console.log('apimsg', res);
  if (+res.data === 200) {
    return;
  }

  // succ 是 false 或者 data 是 200 以外状态码
  if (!res.succ || parseInt(res.data) === +res.data) {
    apiRobot.onerror?.(res.data)
    return
  }

  apiRobot.message(res);
}

export default apiRobot;
